const { chatResponse } = require("./chatRealtime.cjs");
const compare = (a, b) => (Date.parse(b.lastMessageAt) || 0) - (Date.parse(a.lastMessageAt) || 0) || String(b._id).localeCompare(String(a._id));

// One bounded, serialized inbox window is shared by initial load, events and paging.
function createChatInbox({ fetchPage, apply, active }) {
  let rows = [], hasMore = false, atHead = true, hasNew = false, tail = null;
  let queue = Promise.resolve();
  function load(changes = null, more = false) {
    const task = queue.catch(() => {}).then(async () => {
      if (!active() || (more && !hasMore)) return;
      const params = more ? { pageSize: 30, before: tail } : changes ? { ids: changes.map((row) => row.id).join(",") } : { pageSize: 30 };
      if (changes && !changes.length) return;
      const payload = chatResponse(await fetchPage(params));
      if (!active()) return;
      const legacy = typeof payload.hasMore !== "boolean";
      // An older API slot ignores paging params. Keep its complete visible list
      // until the next ready refresh instead of hiding unreachable older rooms.
      const incoming = legacy ? payload.data || [] : (payload.data || []).slice(0, 120);
      if (legacy) {
        rows = incoming; hasMore = false; atHead = true; hasNew = false; tail = null;
        apply({ rows: rows.slice(), hasMore, hasNew });
        return;
      }
      if (!more && !changes) {
        rows = incoming; hasMore = !!payload.hasMore; atHead = true; hasNew = false;
      } else if (more) {
        const known = new Set(rows.map((row) => row._id));
        rows = rows.concat(incoming.filter((row) => !known.has(row._id)));
        if (rows.length > 120) { rows = rows.slice(-120); atHead = false; }
        hasMore = !!payload.hasMore;
      } else {
        const byId = new Map(incoming.map((row) => [row._id, row]));
        for (const change of changes) {
          const row = byId.get(change.id);
          const index = rows.findIndex((item) => item._id === change.id);
          if (!row) { if (index >= 0) rows.splice(index, 1); continue; }
          if (!atHead && change.kind === "message") { hasNew = true; continue; }
          if (index >= 0) rows.splice(index, 1, row);
          else if (atHead && change.kind === "message") rows.push(row);
        }
        rows.sort(compare);
        if (rows.length > 120) { rows = rows.slice(0, 120); hasMore = true; }
      }
      // A live prepend may evict the tail: use the retained tuple, never a stale cursor.
      tail = rows.length ? rows[rows.length - 1].pageCursor : payload.nextBefore || tail;
      apply({ rows: rows.slice(), hasMore, hasNew: hasNew || !atHead });
    });
    queue = task;
    return task;
  }
  return { sync: (changes) => load(changes), more: () => load(null, true) };
}

function createChatRoom({ fetchMessages, markRead, apply, active, unreadField }) {
  let version = null;
  let messages = [];
  async function sync() {
    let payload;
    let responseTime;
    for (let page = 0; page < 5; page++) {
      payload = chatResponse(await fetchMessages(version === null ? undefined : { afterVersion: version }));
      if (!active()) return;
      if (payload.resetRequired) { version = null; continue; }
      if (Object.prototype.hasOwnProperty.call(payload, "responseTime")) responseTime = payload.responseTime;
      if (version === null) messages = payload.data || [];
      else {
        const byId = new Map(messages.map((row) => [row._id, row]));
        for (const row of payload.data || []) byId.set(row._id, row);
        messages = Array.from(byId.values()).slice(-100);
      }
      version = Number.isSafeInteger(payload.nextVersion) ? payload.nextVersion : null;
      if (!payload.hasMore) break;
      if (page === 4) payload = null;
    }
    if (!payload || payload.resetRequired) {
      payload = chatResponse(await fetchMessages());
      if (!active()) return;
      messages = payload.data || [];
      version = Number.isSafeInteger(payload.nextVersion) ? payload.nextVersion : null;
    }
    if (responseTime !== undefined && !Object.prototype.hasOwnProperty.call(payload, "responseTime")) payload = { ...payload, responseTime };
    apply(messages.slice(), payload.conversation, payload);
    const conversation = payload.conversation;
    if (active() && conversation[unreadField] > 0) {
      chatResponse(await markRead({ messageVersion: conversation.messageVersion || 0, lastMessageAt: conversation.lastMessageAt, [unreadField]: conversation[unreadField] }));
    }
  }
  return { sync, reset: () => { version = null; return sync(); } };
}

module.exports = { createChatInbox, createChatRoom };
